import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useT } from '@/lib/i18n/useT';
import type { XkBrand } from '@/lib/api/types';

/**
 * Minimal "create a brand" dialog. Asks only for what the DB requires — the
 * brand `name` (plus an optional industry) — because the rest of the identity
 * (logo, palette, fonts…) is configured right after in Brand Studio.
 *
 * The backend (`POST /co-companies`) auto-sets `parentId`/`companyId` to the
 * caller's agency and auto-creates the `co_company_accesses` row with
 * `xotskiAccess=1`, so a single call makes the brand show up in the switcher.
 * Plan caps are enforced server-side via `@PlanGated('workspace','brand')`;
 * the caller should gate the entry point on `usePlanQuota('maxBrands')` and
 * pass `onQuotaExceeded` to pivot to the upgrade prompt on a late rejection.
 */
export function NewBrandDialog({
  open,
  onOpenChange,
  onCreated,
  onQuotaExceeded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (brand: XkBrand) => void;
  onQuotaExceeded?: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');

  const reset = () => {
    setName('');
    setIndustry('');
  };

  const create = useMutation({
    mutationFn: () =>
      brandsApi.create({
        name: name.trim(),
        industry: industry.trim() || undefined,
      }),
    onSuccess: (brand) => {
      // Optimistically splice the new brand into the cached list BEFORE the
      // caller selects it. OnboardingGate has a "repair stale selection"
      // effect that resets the selection to brands[0] when the selected id
      // isn't in the list yet — without this, selecting the freshly-created
      // brand would be clobbered until the background refetch lands. Writing
      // the cache synchronously makes the id valid in the same render.
      queryClient.setQueryData<{ items: XkBrand[]; total?: number }>(
        queryKeys.brands.all,
        (old) => {
          if (!old) return old;
          if (old.items?.some((b) => b.id === brand.id)) return old;
          return {
            ...old,
            items: [...(old.items ?? []), brand],
            total: (old.total ?? old.items?.length ?? 0) + 1,
          };
        },
      );
      // Reconcile with the server (count, ordering, derived fields).
      queryClient.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('brand.new.created', { name: brand.name ?? name.trim() }) });
      reset();
      onCreated(brand);
    },
    onError: (e: Error) => {
      // Plan cap rejected server-side — pivot to the upgrade prompt instead of
      // a raw error toast. PlanLimitException is HTTP 409 with
      // `code: 'PLAN_LIMIT_EXCEEDED'`; 409 on this endpoint always means the
      // brand cap, so treat the status as a fallback discriminator.
      const apiErr = e as { status?: number; payload?: { code?: string } };
      const isPlanLimit =
        apiErr.payload?.code === 'PLAN_LIMIT_EXCEEDED' ||
        apiErr.payload?.code === 'PLAN_LIMIT_REACHED' ||
        apiErr.status === 409;
      if (isPlanLimit && onQuotaExceeded) {
        onOpenChange(false);
        onQuotaExceeded();
        return;
      }
      toast({ tone: 'error', title: t('brand.new.failed'), description: e.message });
    },
  });

  const canSubmit = name.trim().length >= 2 && !create.isPending;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('brand.new.title')}</DialogTitle>
          <DialogDescription>{t('brand.new.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('brand.new.nameLabel')} <span className="text-[var(--color-accent)]">*</span>
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('brand.new.namePlaceholder')}
              autoFocus
              required
              minLength={2}
              className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
            />
          </label>
          <label className="block">
            <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
              {t('brand.new.industryLabel')}
            </div>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder={t('brand.new.industryPlaceholder')}
              className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-xk-accent)] focus:outline-none"
            />
          </label>
          <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-muted)]">
            {t('brand.new.studioHint')}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
              >
                {t('common.cancel')}
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[#0A0A0A] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {create.isPending ? t('brand.new.creating') : t('brand.new.submit')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
